import type { ReactNode } from "react";
import { createContext, useMemo, useState } from "react";

import {
  type ConsumableMatrixItem,
  type ConsumablesMatrixData,
  type ConsumablesProductDetailsData,
} from "@product-details/containers/ConsumablesProductDetailsContainer/ConsumablesProductDetailsContainer.types";

type ConsumablesProductDetailsProviderProps = {
  children: ReactNode;
  data: ConsumablesProductDetailsData;
};

type ConsumablesProductDetailsContextValue = {
  consumablesProductDetailsComponentData: ConsumablesProductDetailsData;
  currentMatrixItem: ConsumableMatrixItem;
  setCurrentSKU: React.Dispatch<React.SetStateAction<string>>;
};

export const ConsumablesProductDetailsContext = createContext<ConsumablesProductDetailsContextValue>(
  {} as ConsumablesProductDetailsContextValue // useConsumablesProductDetailsContext will throw an error if this context is not provided
);

const findCurrentConsumable = (sku: string, consumablesMatrixData: ConsumablesMatrixData): ConsumableMatrixItem => {
  const consumablesMatrixItemData = consumablesMatrixData.items.find((item) => item.sku === sku);

  if (!consumablesMatrixItemData) {
    throw new Error(`Couldn't find product with SKU: ${sku}`);
  }

  return consumablesMatrixItemData;
};

export const ConsumablesProductDetailsProvider = ({ children, data }: ConsumablesProductDetailsProviderProps) => {
  const [currentSKU, setCurrentSKU] = useState(data.sku);

  const currentMatrixItem = useMemo(() => {
    return findCurrentConsumable(currentSKU, data.consumablesMatrixData);
  }, [currentSKU, data.consumablesMatrixData]);

  if (!data) {
    throw new Error("ConsumablesProductDetailsProvider: data prop is required");
  }

  const contextValue: ConsumablesProductDetailsContextValue = useMemo(
    () => ({
      consumablesProductDetailsComponentData: data,
      currentMatrixItem,
      setCurrentSKU,
    }),
    [data, currentMatrixItem, setCurrentSKU]
  );

  return (
    <ConsumablesProductDetailsContext.Provider value={contextValue}>
      {children}
    </ConsumablesProductDetailsContext.Provider>
  );
};
